import type { TicketBookingPolicy } from "./types";

/**
 * Generates a brief one-line summary of a booking policy for inline display.
 * e.g., "Free cancellation up to 3 days before · Reschedule up to 1 day before"
 */
export function formatPolicyHint(policy: TicketBookingPolicy | null | undefined): string | undefined {
  if (!policy) return undefined;

  const parts: string[] = [];

  // Cancellation hint
  if (!policy.canCancel) {
    parts.push("No cancellation");
  } else if (policy.cancellationTiers && policy.cancellationTiers.length > 0) {
    const bestTier = [...policy.cancellationTiers].sort(
      (a, b) => b.refundPercentage - a.refundPercentage
    )[0];
    if (bestTier) {
      const timeParts: string[] = [];
      if (bestTier.daysBefore > 0) {
        timeParts.push(`${bestTier.daysBefore} ${bestTier.daysBefore > 1 ? "days" : "day"}`);
      }
      if (bestTier.hoursBefore > 0) {
        timeParts.push(`${bestTier.hoursBefore}h`);
      }
      const timeText = timeParts.join(" ");
      if (bestTier.refundPercentage === 100) {
        parts.push(`Free cancellation up to ${timeText} before`);
      } else {
        parts.push(`${bestTier.refundPercentage}% refund up to ${timeText} before`);
      }
    }
  }

  // Reschedule hint
  if (!policy.canReschedule) {
    parts.push("No reschedule");
  } else {
    const timeParts: string[] = [];
    if (policy.rescheduleDaysBefore > 0) {
      timeParts.push(`${policy.rescheduleDaysBefore} ${policy.rescheduleDaysBefore > 1 ? "days" : "day"}`);
    }
    if (policy.rescheduleHoursBefore > 0) {
      timeParts.push(`${policy.rescheduleHoursBefore}h`);
    }
    if (timeParts.length > 0) {
      parts.push(`Reschedule up to ${timeParts.join(" ")} before`);
    }
  }

  return parts.length > 0 ? parts.join(" · ") : undefined;
}
